import React, { FunctionComponent } from 'react';
import { BasicCheckboxGroup } from '../../basic-inputs';
import { TooltipPosition, TooltipSize } from '../../tooltip';
import FormField from '../FormField';
import { FormFieldProps } from '../types';
import { CheckboxFieldProps } from './CheckboxField';

interface GroupProps {
  id: string;
  inline?: boolean;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  error?: boolean;
  errorMessage?: string;
  helpText?: string;
  helpTextPosition?: 'after-label' | 'below-field';
  helperTextToolTipConfig?: {
    size?: TooltipSize;
    position?: TooltipPosition;
  };
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
}

type Props = FormFieldProps & GroupProps;

type GroupFieldProps =
  | ({ name: string } & GroupProps)
  | ({ name: string } & GroupProps & FormFieldProps);
const CheckboxGroup: React.FunctionComponent<React.PropsWithChildren<Props>> = (props) => {
  const {
    children,
    id,
    inline,
    label,
    required,
    disabled,
    error,
    errorMessage,
    helpText,
    helpTextPosition,
    helperTextToolTipConfig,
    onBlur,
    onChange,
    form
  } = props;

  const handleChange = (event: any) => {
    const target = event.currentTarget;
    const valueArray = form.values[id] || [];

    if (target.checked) {
      valueArray.push(target.id);
    } else {
      valueArray.splice(valueArray.indexOf(target.id), 1);
    }

    form.setFieldValue(id, valueArray);
    if (onChange) {
      onChange(event);
    }
  };

  const handleBlur = (event: any) => {
    form.setFieldTouched(id, true);
    if (onBlur) {
      onBlur(event);
    }
  };

  return (
    <BasicCheckboxGroup
      error={error}
      errorMessage={errorMessage}
      helpText={helpText}
      helpTextPosition={helpTextPosition}
      helperTextToolTipConfig={helperTextToolTipConfig}
      disabled={disabled}
      inline={inline}
      label={label}
      required={required}>
      {React.Children.map(children, child => {
        if (React.isValidElement<CheckboxFieldProps>(child)) {
          return React.cloneElement(child, {
            field: {
              name: child.props.name,
              onBlur: handleBlur,
              onChange: handleChange,
              value: form.values[id].includes(child.props.id)
            },
            onChange: handleChange,
            onBlur: handleBlur
          });
        } else {
          return null;
        }
      })}
    </BasicCheckboxGroup>
  );
};

const CheckboxGroupField: React.FC<GroupFieldProps> = props => (
  <FormField component={CheckboxGroup} {...props} />
);

export default CheckboxGroupField;
